"use client"

import { useCallback, useMemo, useState, useTransition } from "react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import {
  ArrowRight,
  Check,
  Clock3,
  Globe2,
  Loader2,
  Search,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react"
import { Button } from "@modules/components/ui/button"
import { trackYohoEvent } from "@/lib/analytics/track"
import { cn } from "@/lib/util"
import { resolvePreferredProductHref } from "@/lib/product-page-links"

type CountryOption = {
  iso2: string
  name: string
  nameEN: string
}

type BuyCountrySelectorPageProps = {
  copy: BuyCountrySelectorCopy
  countries: CountryOption[]
  initialSelectedIso2s: string[]
  lng: string
  passThroughQueryString?: string
}

export type BuyCountrySelectorCopy = {
  allTitle: string
  continueLabel: string
  disabledLabel: string
  loadingLabel: string
  eyebrow: string
  heroDescription: string
  heroTitle: string
  noResults: string
  searchPlaceholder: string
  searchResultsTitle: string
  selectedCountTemplate: string
  summaryEmpty: string
  summarySelected: string
  summaryTitle: string
  supportOne: string
  supportThree: string
  supportTwo: string
}

const ZH_PINYIN_BOUNDARIES: Array<[string, string]> = [
  ["A", "阿"],
  ["B", "八"],
  ["C", "嚓"],
  ["D", "咑"],
  ["E", "妸"],
  ["F", "发"],
  ["G", "旮"],
  ["H", "哈"],
  ["J", "讥"],
  ["K", "咔"],
  ["L", "垃"],
  ["M", "妈"],
  ["N", "拏"],
  ["O", "噢"],
  ["P", "妑"],
  ["Q", "七"],
  ["R", "呥"],
  ["S", "仨"],
  ["T", "它"],
  ["W", "哇"],
  ["X", "夕"],
  ["Y", "丫"],
  ["Z", "帀"],
]
const zhCollator = new Intl.Collator("zh-Hans-CN")

function getFlagUrl(iso2: string): string {
  return `https://flagcdn.com/${iso2.toLowerCase()}.svg`
}

function normalizeLocale(lng: string): string {
  return lng.toLowerCase()
}

function isZhLocale(lng: string): boolean {
  return normalizeLocale(lng).startsWith("zh")
}

function getCountryGroupLetter(country: CountryOption, isZh: boolean): string {
  const firstChar = Array.from(country.name.trim())[0]
  if (!firstChar) return "#"

  if (!isZh) {
    const letter = firstChar.toLocaleUpperCase()
    return /^[A-Z]$/.test(letter) ? letter : "#"
  }

  if (/^[A-Za-z]$/.test(firstChar)) return firstChar.toUpperCase()

  let group = "A"
  for (const [letter, boundary] of ZH_PINYIN_BOUNDARIES) {
    if (zhCollator.compare(firstChar, boundary) >= 0) {
      group = letter
    } else {
      break
    }
  }
  return group
}

function formatSelectedCount(template: string, count: number): string {
  if (!template.includes("{{count}}")) {
    throw new Error("[buy] selected count template missing {{count}}")
  }

  return template.replaceAll("{{count}}", String(count))
}

export default function BuyCountrySelectorPage({
  copy,
  countries,
  initialSelectedIso2s,
  lng,
  passThroughQueryString,
}: BuyCountrySelectorPageProps) {
  const router = useRouter()
  const isZh = isZhLocale(lng)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedIso2s, setSelectedIso2s] = useState<string[]>(initialSelectedIso2s)
  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  const isSearching = normalizedSearchQuery.length > 0

  const countryByIso2 = useMemo(() => {
    return new Map(countries.map((country) => [country.iso2, country]))
  }, [countries])


  const filteredCountries = useMemo(() => {
    if (!normalizedSearchQuery) return countries

    return countries.filter((country) => {
      return (
        country.name.toLowerCase().includes(normalizedSearchQuery) ||
        country.nameEN.toLowerCase().includes(normalizedSearchQuery) ||
        country.iso2.toLowerCase().includes(normalizedSearchQuery)
      )
    })
  }, [countries, normalizedSearchQuery])

  const groupedCountries = useMemo(() => {
    const groups: Record<string, CountryOption[]> = {}

    for (const country of countries) {
      const letter = getCountryGroupLetter(country, isZh)
      if (!groups[letter]) groups[letter] = []
      groups[letter].push(country)
    }

    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
  }, [countries, isZh])

  const selectedCountries = useMemo(() => {
    return selectedIso2s.map((iso2) => countryByIso2.get(iso2)).filter(Boolean) as CountryOption[]
  }, [countryByIso2, selectedIso2s])

  const selectedCount = useMemo(() => {
    return formatSelectedCount(copy.selectedCountTemplate, selectedIso2s.length)
  }, [copy.selectedCountTemplate, selectedIso2s.length])

  const targetHref = useMemo(() => {
    if (selectedIso2s.length === 0) return undefined
    return resolvePreferredProductHref(lng, selectedIso2s, passThroughQueryString)
  }, [lng, passThroughQueryString, selectedIso2s])

  const toggleCountry = useCallback((iso2: string) => {
    const country = countryByIso2.get(iso2)
    if (!country) {
      throw new Error(`[buy] country not found for ${iso2}`)
    }

    trackYohoEvent("country_picker_select", {
      country_code: iso2,
      country_name: country.name,
      source: "buy_country_selector",
    })

    setSelectedIso2s((current) => {
      if (current.includes(iso2)) {
        return current.filter((item) => item !== iso2)
      }

      return [...current, iso2]
    })
  }, [countryByIso2])

  // useTransition surfaces an `isPending` boolean while the next route is
  // loading. router.push inside startTransition lets the button stay in a
  // disabled "正在加载…" state until the product page mounts, mirroring the
  // checkout "立即购买" button's isSubmitting pattern.
  const [isNavigating, startNavigation] = useTransition()

  const handleConfirm = useCallback(() => {
    if (!targetHref) return
    if (isNavigating) return
    startNavigation(() => {
      router.push(targetHref)
    })
  }, [isNavigating, router, targetHref])

  return (
    <div className="relative min-h-screen overflow-hidden bg-white text-stone-950">
      <div className="relative mx-auto flex max-w-[1180px] flex-col px-5 py-9 md:px-8 md:py-12">
        <header className="mb-8 max-w-3xl">
          <p className="mb-3 text-[12px] font-bold uppercase tracking-[0.2em] text-orange-600">
            {copy.eyebrow}
          </p>
          <h1 className="text-[36px] font-extrabold leading-tight tracking-[-0.035em] text-stone-950 md:text-[56px]">
            {copy.heroTitle}
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-7 text-stone-600 md:text-[17px]">
            {copy.heroDescription}
          </p>
        </header>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
          <section className="rounded-[28px] border border-stone-200 bg-white p-4 shadow-[0_18px_60px_-38px_rgba(28,25,23,0.22)] md:p-6">
            <div className="mb-5 flex flex-col gap-3 border-b border-stone-100 pb-5 md:flex-row md:items-center md:justify-between">
              <label className="flex min-h-[50px] flex-1 items-center gap-3 rounded-2xl border border-stone-200 bg-stone-50/80 px-4 transition focus-within:border-orange-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-orange-100">
                <Search className="h-4 w-4 text-stone-400" aria-hidden="true" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={copy.searchPlaceholder}
                  className="w-full bg-transparent text-[14px] text-stone-900 outline-none placeholder:text-stone-400"
                />
              </label>

              <div className="shrink-0 rounded-full bg-orange-50 px-4 py-2 text-[13px] font-bold text-orange-700">
                {selectedCount}
              </div>
            </div>

            <section>
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-[18px] font-extrabold tracking-tight text-stone-950">
                  {isSearching ? copy.searchResultsTitle : copy.allTitle}
                </h2>
                <span className="text-[12px] font-semibold text-stone-400">
                  {isSearching ? filteredCountries.length : countries.length} / {countries.length}
                </span>
              </div>

              <div className="space-y-6">
                {filteredCountries.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-stone-200 px-6 py-16 text-center text-[14px] text-stone-400">
                    {copy.noResults}
                  </div>
                ) : isSearching ? (
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {filteredCountries.map((country) => (
                      <CountryCard
                        key={country.iso2}
                        country={country}
                        selected={selectedIso2s.includes(country.iso2)}
                        onToggle={toggleCountry}
                      />
                    ))}
                  </div>
                ) : (
                  groupedCountries.map(([letter, options]) => (
                    <section key={letter}>
                      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">
                        {letter}
                      </div>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                        {options.map((country) => (
                          <CountryCard
                            key={country.iso2}
                            country={country}
                            selected={selectedIso2s.includes(country.iso2)}
                            onToggle={toggleCountry}
                          />
                        ))}
                      </div>
                    </section>
                  ))
                )}
              </div>
            </section>
          </section>

          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-[28px] border border-stone-200 bg-white p-5 shadow-[0_18px_60px_-40px_rgba(28,25,23,0.35)]">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
                  <Globe2 className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <h2 className="text-[20px] font-extrabold tracking-tight text-stone-950">
                    {copy.summaryTitle}
                  </h2>
                  <p className="text-[13px] font-medium text-stone-500">
                    {selectedIso2s.length > 0 ? copy.summarySelected : copy.summaryEmpty}
                  </p>
                </div>
              </div>

              <SelectedCountries
                countries={selectedCountries}
                emptyLabel={copy.summaryEmpty}
                onRemove={toggleCountry}
              />

              <div className="my-5 space-y-3 rounded-2xl bg-stone-50 p-4 text-[13px] font-semibold text-stone-600">
                <SummaryPoint icon={Clock3} label={copy.supportOne} />
                <SummaryPoint icon={ShieldCheck} label={copy.supportTwo} />
                <SummaryPoint icon={UserRound} label={copy.supportThree} />
              </div>

              <Button
                type="button"
                disabled={!targetHref || isNavigating}
                onClick={handleConfirm}
                aria-busy={isNavigating}
                className="h-12 w-full rounded-2xl bg-orange-500 text-[15px] font-extrabold text-white shadow-[0_14px_34px_-18px_rgba(234,88,12,0.75)] hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400 disabled:shadow-none"
              >
                {isNavigating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    {copy.loadingLabel}
                  </>
                ) : (
                  <>
                    {targetHref ? copy.continueLabel : copy.disabledLabel}
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </>
                )}
              </Button>
            </div>
          </aside>
        </div>
      </div>

      <div className="sticky bottom-0 z-20 border-t border-stone-200 bg-white/96 backdrop-blur lg:hidden">
        <div className="mx-auto flex max-w-[1180px] flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between md:px-8">
          <div className="text-[13px] font-semibold text-stone-500">
            {selectedIso2s.length > 0 ? selectedCount : copy.summaryEmpty}
          </div>
          <Button
            type="button"
            disabled={!targetHref || isNavigating}
            onClick={handleConfirm}
            aria-busy={isNavigating}
            className="h-12 w-full rounded-2xl bg-orange-500 text-[15px] font-extrabold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400 md:w-auto"
          >
            {isNavigating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                {copy.loadingLabel}
              </>
            ) : (
              targetHref ? copy.continueLabel : copy.disabledLabel
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

function CountryCard({
  country,
  selected,
  onToggle,
  compact = false,
}: {
  country: CountryOption
  selected: boolean
  onToggle: (iso2: string) => void
  compact?: boolean
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(country.iso2)}
      className={cn(
        "group flex items-center justify-between rounded-2xl border text-left transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500",
        compact ? "px-3 py-2.5" : "px-3.5 py-2.5",
        selected
          ? "border-orange-300 bg-orange-50 shadow-[0_10px_24px_-18px_rgba(234,88,12,0.7)]"
          : "border-stone-200 bg-white hover:border-orange-200 hover:bg-orange-50/35"
      )}
    >
      <span className="flex min-w-0 items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-stone-50">
          <Image
            src={getFlagUrl(country.iso2)}
            alt=""
            width={22}
            height={22}
            className="rounded-[3px] object-cover"
          />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[14px] font-bold text-stone-900">
            {country.name}
          </span>
          <span className="mt-0.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-400">
            {country.iso2}
          </span>
        </span>
      </span>
      <span
        className={cn(
          "ml-3 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition",
          selected
            ? "border-orange-500 bg-orange-500 text-white"
            : "border-stone-200 text-transparent group-hover:border-orange-300"
        )}
      >
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    </button>
  )
}

function SelectedCountries({
  countries,
  emptyLabel,
  onRemove,
}: {
  countries: CountryOption[]
  emptyLabel: string
  onRemove: (iso2: string) => void
}) {
  if (countries.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50 px-4 py-8 text-center text-[14px] font-semibold text-stone-400">
        {emptyLabel}
      </div>
    )
  }

  return (
    <div className="flex max-h-44 flex-wrap gap-2 overflow-auto rounded-2xl border border-stone-100 bg-stone-50 p-3">
      {countries.map((country) => (
        <button
          key={country.iso2}
          type="button"
          onClick={() => onRemove(country.iso2)}
          className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-3 py-2 text-[13px] font-bold text-stone-700 transition hover:border-orange-400 hover:text-orange-700"
        >
          <Image
            src={getFlagUrl(country.iso2)}
            alt=""
            width={18}
            height={18}
            className="rounded-[2px] object-cover"
          />
          {country.name}
          <X className="h-3.5 w-3.5 text-stone-400" aria-hidden="true" />
        </button>
      ))}
    </div>
  )
}

function SummaryPoint({ icon: Icon, label }: { icon: typeof Clock3; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-orange-600" aria-hidden="true" />
      {label}
    </div>
  )
}
